import { REVIEW_STATUS_FILTER, ROW_STATUS } from '../constants/reviewFilters.js';
import { searchAllColumns } from './searchAllColumns.js';

/** Filter state of the review step: the four dropdowns (arrays) and the status chip. */
export const EMPTY_REVIEW_FILTERS = Object.freeze({
  access: [],
  missing: [],
  check: [],
  fileRole: [],
  status: REVIEW_STATUS_FILTER.ALL,
});

const lower = (value) => String(value).toLowerCase();
const hasRoles = (row) => (row.access?.role_ids.length ?? 0) > 0;

const ACCESS_TESTS = {
  none: (row) => !row.access && row.status !== ROW_STATUS.UPDATE,
  assigned: (row) => row.access !== null,
  admin: (row) => row.access?.user_type === 'admin',
  no_studio: (row, roles) =>
    hasRoles(row) &&
    row.access.role_ids.every((id) => (roles.find((r) => r.id === id)?.studios ?? []).length === 0),
};
const MISSING_TESTS = {
  // A member that already exists keeps access we cannot see here: never "missing a role".
  role: (row) => row.status !== ROW_STATUS.UPDATE && !hasRoles(row),
  job_title: (row) => !row.person.job_title,
  location: (row) => !row.person.location,
  manager: (row) => !row.person.reports_to,
};

const matchesAny = (values, test) => values.length === 0 || values.some(test);

/**
 * Rows that pass the search and the four dropdowns (every group must match; inside a group
 * ANY chosen value may). The status chip is applied last, so the counters stay meaningful.
 *
 * @param {object[]} rows
 * @param {typeof EMPTY_REVIEW_FILTERS} filters
 * @param {{ roles: Array<{ id: string, studios: string[] }> }} catalog
 * @param {string} [search]
 */
export function applyReviewFilters(rows, filters, catalog, search = '') {
  return rows
    .filter((row) => searchAllColumns(row, search))
    .filter((row) => matchesAny(filters.access, (key) => ACCESS_TESTS[key]?.(row, catalog.roles)))
    .filter((row) => matchesAny(filters.missing, (key) => MISSING_TESTS[key]?.(row)))
    .filter((row) =>
      matchesAny(filters.check, (code) => row.issues.some((issue) => issue.code === code)),
    )
    .filter((row) =>
      matchesAny(filters.fileRole, (name) =>
        row.accessFromFile.roles.some((role) => lower(role) === lower(name)),
      ),
    )
    .filter((row) => filters.status === REVIEW_STATUS_FILTER.ALL || row.status === filters.status);
}

/** Counter chips: All / New / Updates / Errors. */
export function countByStatus(rows) {
  const count = (status) => rows.filter((row) => row.status === status).length;
  return {
    all: rows.length,
    new: count(ROW_STATUS.NEW),
    update: count(ROW_STATUS.UPDATE),
    error: count(ROW_STATUS.ERROR),
  };
}
